import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const STORAGE_KEY = "auditPopupDismissed";
const DELAY_MS = 10000;

export function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const reduced = useReducedMotion();

  useEffect(() => {
    if (location.pathname === "/audit") return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY) === "true") return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      if (sessionStorage.getItem(STORAGE_KEY) === "true") return;
      triggered = true;
      setOpen(true);
    };

    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) trigger();
    };

    const timer = window.setTimeout(trigger, DELAY_MS);
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [location.pathname]);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
  };

  const handleCTA = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
    navigate("/audit");
  };

  const bullets = [
    "See where leads are slipping through",
    "Get a custom look at what your pipeline could produce",
    "Walk away with real next steps — no obligation",
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          initial={reduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="relative w-full sm:max-w-[480px] bg-background border border-border shadow-2xl rounded-2xl p-6 sm:p-8"
            initial={reduced ? { opacity: 1, scale: 1, y: 0, filter: "blur(0px)" } : { opacity: 0, scale: 0.92, y: 16, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8, filter: "blur(4px)" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              onClick={dismiss}
              aria-label="Close"
              className="absolute top-3 right-3 p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="text-xs font-semibold tracking-widest uppercase text-primary">
              Limited Spots Available
            </p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-foreground leading-tight tracking-tight">
              Get a Free 1-on-1 Pipeline Review
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              In 30 minutes, we'll walk through your current intake process and show you exactly where your firm could be bringing in more clients.
            </p>

            <ul className="mt-5 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-foreground">{b}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={handleCTA}
              className="mt-6 w-full bg-primary text-primary-foreground font-semibold text-base px-6 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-shadow"
            >
              Claim My Free Audit
            </button>

            <button
              onClick={dismiss}
              className="mt-3 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              No thanks, I'll figure it out on my own
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
